import { useCallback, useEffect, useRef, useState } from 'react';
import { api, apiDownloadPost, apiUpload } from '../../api/client';
import type { RepresentacionGenerarInput, VerifactuPanelStatus, VerifactuRegistro } from '../../api/types';
import { Alert, Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input, Select } from '../../components/ui/Input';
import { CONTACT_EMAIL } from '../../config/contact';

interface Props {
  licenseId: number;
  isDemo: boolean;
}

function representacionTone(value: string | null | undefined): 'success' | 'warning' | 'danger' | 'neutral' {
  if (value === 'Correcto') return 'success';
  if (value === 'Rechazado') return 'danger';
  if (value === 'Inexistente') return 'warning';
  return 'neutral';
}

function splitStreetAddress(direccion: string | null | undefined): { calle: string; numero: string } {
  const trimmed = (direccion ?? '').trim();
  if (!trimmed) return { calle: '', numero: 's/n' };
  const match = trimmed.match(/^(.+?)[,\s]+(\d+\S*)$/);
  if (match) {
    return { calle: match[1].trim(), numero: match[2].trim() };
  }
  return { calle: trimmed, numero: 's/n' };
}

export function VerifactuLicensePanel({ licenseId, isDemo }: Props) {
  const [status, setStatus] = useState<VerifactuPanelStatus | null>(null);
  const [registros, setRegistros] = useState<VerifactuRegistro[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showRepresentacionForm, setShowRepresentacionForm] = useState(false);
  const [repForm, setRepForm] = useState<RepresentacionGenerarInput>({
    tipo_entidad: 'juridica',
  });
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [panelStatus, panelRegistros] = await Promise.all([
        api.get<VerifactuPanelStatus>(`/api/verifactu/panel/licenses/${licenseId}`),
        api.get<{ registros: VerifactuRegistro[] }>(
          `/api/verifactu/panel/licenses/${licenseId}/registros?limit=8`
        ),
      ]);
      setStatus(panelStatus);
      setRegistros(panelRegistros.registros ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cargar Verifactu');
    } finally {
      setLoading(false);
    }
  }, [licenseId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!status?.registered || !status.nif) return;
    const { calle, numero } = splitStreetAddress(status.direccion);
    const tipo = status.representation_type ?? 'fisica';
    const representativeAddress = splitStreetAddress(status.direccion);
    setRepForm({
      tipo_entidad: tipo,
      nombre: tipo === 'fisica' ? status.representative_name ?? '' : undefined,
      municipio: tipo === 'fisica' ? status.poblacion ?? '' : undefined,
      calle: tipo === 'fisica' ? calle : undefined,
      numero: tipo === 'fisica' ? numero : undefined,
      nombre_entidad: tipo === 'juridica' ? status.nombre ?? '' : undefined,
      municipio_entidad: status.poblacion ?? '',
      calle_entidad: calle,
      numero_entidad: numero,
      nombre_representante: tipo === 'juridica' ? status.representative_name ?? '' : undefined,
      nif_representante: tipo === 'juridica' ? status.representative_nif ?? '' : undefined,
      municipio_representante: status.poblacion ?? '',
      calle_representante: representativeAddress.calle,
      numero_representante: representativeAddress.numero,
    });
  }, [
    status?.nif,
    status?.nombre,
    status?.registered,
    status?.direccion,
    status?.poblacion,
    status?.representation_type,
    status?.representative_name,
    status?.representative_nif,
  ]);

  async function toggleModule(enabled: boolean) {
    if (isDemo) return;
    setBusy(true);
    setMessage('');
    setError('');
    try {
      const result = await api.patch<{ verifactu_enabled: boolean; message?: string }>(
        `/api/verifactu/licenses/${licenseId}`,
        { verifactu_enabled: enabled }
      );
      setMessage(
        result.message ??
          (enabled
            ? 'VeriFactu activado. Está incluido en tu plan y no cambia tu cuota.'
            : 'Módulo Verifactu desactivado.')
      );
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo actualizar el módulo');
    } finally {
      setBusy(false);
    }
  }

  async function downloadRepresentacion() {
    if (isDemo || !status?.nif) return;
    setBusy(true);
    setMessage('');
    setError('');
    try {
      await apiDownloadPost(
        `/api/verifactu/panel/licenses/${licenseId}/representacion/generar`,
        repForm,
        `representacion-${status.nif}.pdf`
      );
      setMessage('Modelo de representación descargado. Fírmalo con certificado digital y súbelo aquí.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo generar el PDF');
    } finally {
      setBusy(false);
    }
  }

  async function uploadPdf(file: File) {
    if (isDemo) return;
    setBusy(true);
    setMessage('');
    setError('');
    try {
      const form = new FormData();
      form.append('file', file);
      const result = await apiUpload<{ message?: string; representacion?: string }>(
        `/api/verifactu/panel/licenses/${licenseId}/representacion`,
        form
      );
      setMessage(result.message ?? 'PDF enviado a Verifacti.');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo subir el PDF');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  if (loading) {
    return <p className="muted" style={{ marginTop: '1rem' }}>Cargando Verifactu…</p>;
  }

  if (!status?.configured) {
    return (
      <div style={{ marginTop: '1rem' }}>
        <Alert tone="info">VeriFactu está incluido en tu plan. El servicio está pendiente de configuración; contacta con Hosty.</Alert>
      </div>
    );
  }

  return (
    <div className="verifactu-panel" style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--border, #e5e7eb)' }}>
      <h4 style={{ margin: '0 0 0.5rem' }}>Verifactu (Hacienda)</h4>
      <p className="muted" style={{ marginTop: 0 }}>
        Incluido en tu plan, sin suplemento. Completa los datos fiscales de la licencia y activa el envío cuando estés listo.
      </p>

      {message ? <Alert tone="success">{message}</Alert> : null}
      {error ? <Alert tone="danger">{error}</Alert> : null}

      <div className="license-card__meta" style={{ marginTop: '0.75rem' }}>
        <Badge tone={status.verifactu_enabled ? 'success' : 'neutral'}>
          {status.verifactu_enabled ? (status.entorno === 'test' ? 'Activo en pruebas' : status.listo_produccion ? 'Listo para emitir' : 'Representación pendiente') : 'Pendiente de activar'}
        </Badge>
        {status.registered && status.nif ? (
          <>
            <span>NIF: {status.nif}</span>
            <span>Entorno: {status.entorno ?? 'test'}</span>
            <Badge tone={representacionTone(status.representacion)}>
              Representación: {status.representacion ?? '—'}
            </Badge>
          </>
        ) : (
          <span className="muted">Completa los datos fiscales de esta licencia antes de activar VeriFactu.</span>
        )}
      </div>

      {status.registered ? (
        <p className="muted">
          Registros: {status.registros_total ?? 0} · Correctos: {status.registros_correctos ?? 0} · Pendientes:{' '}
          {status.registros_pendientes ?? 0}
        </p>
      ) : null}

      {!isDemo ? (
        <div className="license-card__actions" style={{ marginTop: '0.75rem' }}>
          {status.verifactu_enabled ? (
            <Button variant="secondary" size="sm" disabled={busy} onClick={() => void toggleModule(false)}>
              Desactivar Verifactu
            </Button>
          ) : (
            <Button variant="secondary" size="sm" disabled={busy || status.can_configure === false || !(status.profile_complete || status.registered)} onClick={() => void toggleModule(true)}>
              Activar VeriFactu
            </Button>
          )}
          {status.registered && status.representacion !== 'Correcto' ? (
            <>
              <Button
                variant="secondary"
                size="sm"
                disabled={busy}
                onClick={() => setShowRepresentacionForm((value) => !value)}
              >
                {showRepresentacionForm ? 'Ocultar formulario' : 'Descargar modelo PDF'}
              </Button>
              <input
                ref={fileRef}
                type="file"
                accept="application/pdf,.pdf"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void uploadPdf(file);
                }}
              />
              <Button variant="secondary" size="sm" disabled={busy} onClick={() => fileRef.current?.click()}>
                Subir PDF firmado
              </Button>
            </>
          ) : null}
          <Button variant="secondary" size="sm" disabled={busy} onClick={() => void load()}>
            Actualizar
          </Button>
        </div>
      ) : null}

      {status.registered && status.representacion !== 'Correcto' && showRepresentacionForm ? (
        <div
          className="verifactu-rep-form"
          style={{
            marginTop: '0.75rem',
            padding: '0.75rem',
            border: '1px solid var(--border, #e5e7eb)',
            borderRadius: '8px',
            display: 'grid',
            gap: '0.5rem',
          }}
        >
          <p className="muted" style={{ margin: 0 }}>
            Revisa quién figura como otorgante antes de descargar el documento.
          </p>
          <Select
            label="Tipo de otorgante"
            value={repForm.tipo_entidad}
            onChange={(event) =>
              setRepForm((previous) => ({
                ...previous,
                tipo_entidad: event.target.value as 'fisica' | 'juridica',
              }))
            }
            options={[
              { value: 'fisica', label: 'Persona física / autónomo' },
              { value: 'juridica', label: 'Persona jurídica / sociedad' },
            ]}
          />
          {repForm.tipo_entidad === 'fisica' ? (
            <>
              <Input
                label="Nombre y apellidos del titular"
                value={repForm.nombre ?? ''}
                onChange={(event) =>
                  setRepForm((previous) => ({ ...previous, nombre: event.target.value }))
                }
                required
              />
              <Input
                label="Municipio fiscal del titular"
                value={repForm.municipio ?? ''}
                onChange={(event) =>
                  setRepForm((previous) => ({ ...previous, municipio: event.target.value }))
                }
                required
              />
              <div className="grid-2">
                <Input
                  label="Vía pública del titular"
                  value={repForm.calle ?? ''}
                  onChange={(event) =>
                    setRepForm((previous) => ({ ...previous, calle: event.target.value }))
                  }
                  required
                />
                <Input
                  label="Número"
                  value={repForm.numero ?? ''}
                  onChange={(event) =>
                    setRepForm((previous) => ({ ...previous, numero: event.target.value }))
                  }
                  required
                />
              </div>
            </>
          ) : (
            <>
              <Input
                label="Razón social"
                value={repForm.nombre_entidad ?? ''}
                onChange={(event) =>
                  setRepForm((previous) => ({ ...previous, nombre_entidad: event.target.value }))
                }
                required
              />
              <Input
                label="Nombre y apellidos del representante legal"
                value={repForm.nombre_representante ?? ''}
                onChange={(event) =>
                  setRepForm((previous) => ({
                    ...previous,
                    nombre_representante: event.target.value,
                  }))
                }
                required
              />
              <Input
                label="NIF del representante legal"
                value={repForm.nif_representante ?? ''}
                onChange={(event) =>
                  setRepForm((previous) => ({
                    ...previous,
                    nif_representante: event.target.value.toUpperCase(),
                  }))
                }
                required
              />
              <Input
                label="Municipio fiscal del representante"
                value={repForm.municipio_representante ?? ''}
                onChange={(event) =>
                  setRepForm((previous) => ({
                    ...previous,
                    municipio_representante: event.target.value,
                  }))
                }
                required
              />
              <div className="grid-2">
                <Input
                  label="Vía pública del representante"
                  value={repForm.calle_representante ?? ''}
                  onChange={(event) =>
                    setRepForm((previous) => ({
                      ...previous,
                      calle_representante: event.target.value,
                    }))
                  }
                  required
                />
                <Input
                  label="Número"
                  value={repForm.numero_representante ?? ''}
                  onChange={(event) =>
                    setRepForm((previous) => ({
                      ...previous,
                      numero_representante: event.target.value,
                    }))
                  }
                  required
                />
              </div>
            </>
          )}
          <Button variant="secondary" size="sm" disabled={busy} onClick={() => void downloadRepresentacion()}>
            Descargar PDF cumplimentado
          </Button>
        </div>
      ) : null}

      {status.registered && status.representacion !== 'Correcto' ? (
        <p className="muted" style={{ marginTop: '0.5rem' }}>
          Trámite único por NIF: descarga el modelo, fírmalo con certificado digital (AutoFirma, FNMT o Adobe Reader)
          y súbelo aquí. Si no puedes subirlo, envía el PDF firmado a{' '}
          <a href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(`Representación Verifactu ${status.nif ?? ''}`)}`}>
            {CONTACT_EMAIL}
          </a>{' '}
          indicando el NIF y el nombre fiscal. Si lo completas en test, también vale para producción.
        </p>
      ) : null}

      {registros.length > 0 ? (
        <div style={{ marginTop: '0.75rem', overflowX: 'auto' }}>
          <table className="ui-table" style={{ fontSize: '0.85rem' }}>
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Factura</th>
                <th>Total</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {registros.map((r) => (
                <tr key={r.uuid}>
                  <td>{String(r.created_at ?? '').slice(0, 10)}</td>
                  <td>
                    {r.serie}-{r.numero}
                  </td>
                  <td>{Number(r.importe_total).toFixed(2)} €</td>
                  <td>{r.estado}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
